/**
 * Wochenplan-Seite — Phase N (Kapitel 19, Entscheidung #22): pro-Standort-Grid
 * mit Tap-Chip-Picker statt firmenweiter Dropdown-Tabelle.
 *
 * Umfang: genau EIN Standort pro Ansicht (bei mehreren verwalteten Standorten
 * ein einfacher Umschalter neben der KW-Navigation, Kapitel 19.3). Zeilen =
 * aktive Mitarbeiter mit `LocationMembership` an diesem Standort (ersetzt die
 * alte, firmenweite `allowedLocationIds`-Prüfung aus v1 — Kapitel 19.2/19.6).
 * Zellen sind Chips statt `<select>`: ein Tap öffnet einen Picker (auf schmalen
 * Bildschirmen als Bottom-Sheet, technisch dieselbe Modal-Darstellung wie im
 * bestehenden `ShiftFormModal`/`LocationsAdminPage`) mit „— kein Dienst",
 * „aktuell behalten" (falls zutreffend), einem Chip je aktiver Vorlage dieses
 * Standorts (wiederverwendet: `ChipButton` aus `ShiftFormModal`) und einem
 * neuen „Eigene Zeit"-Chip für eine einmalige, freie Zeitspanne ohne Vorlage
 * (Entscheidung #22c). Der „▾"-Button im Spaltenkopf öffnet denselben Picker,
 * wendet die Auswahl aber auf die ganze sichtbare Spalte an ("Spalte füllen").
 *
 * Unverändert aus v1 (Kapitel 19.5, bewusst nicht neu erfunden): Diff-basiertes
 * Veröffentlichen (`baseline` vs. `choices`, nur geänderte Zellen werden als
 * `create`/`update`/`delete` weggeschrieben), „Vorwoche kopieren", Sperren bei
 * `status === 'done'` / gesetztem `settledAt` / mehreren Schichten am selben
 * Tag, Live-Stunden + Lohn je Zeile/Spalte/Gesamt (inkl. `wageIncomplete`-Stern
 * aus Phase G, wenn der Lohn für mind. eine Person nicht sichtbar ist),
 * ISO-Wochen-Navigation. Keine neuen Backend-Endpunkte/-Modelle — reine
 * Frontend-/Query-Umstellung, wie in der Roadmap (Kapitel 12, Phase N)
 * festgelegt.
 *
 * Bewusst über den wörtlichen Kapitel-19-Text hinausgehend, aber im gleichen
 * Sicherheitsgeist ergänzt: hat ein:e Mitarbeiter:in an einem Tag GENAU eine
 * Schicht, aber an einem ANDEREN Standort (z. B. durch standortübergreifendes
 * Einspringen, Kapitel 11), zeigt die Zelle in diesem Standort-Plan das als
 * gesperrte "an <Standort>"-Info statt als leer/editierbar an — verhindert
 * versehentliche Doppelbuchung, ohne dass der Schichtplan selbst dafür eine
 * neue Prüfung bräuchte (die Mehrfach-Sperre aus v1 bleibt zusätzlich
 * bestehen, wenn es an diesem Tag bereits mehrere Schichten gibt).
 *
 * Ebenfalls ergänzt (Kapitel 19.4, letzter Absatz — optionaler, nicht
 * blockierender Hinweis): liegt eine gewählte Vorlage oder eine „Eigene Zeit"
 * außerhalb der in Phase B gepflegten `Location.openingHours` des jeweiligen
 * Wochentags, bekommt der Chip im Picker ein kleines Warnsymbol. Reine
 * Anzeige, kein Blocker — fehlt eine Öffnungszeiten-Angabe für den Standort
 * oder Wochentag, wird bewusst NICHT gewarnt (kein Zwang, das Feld zu
 * pflegen).
 *
 * Phase DD (Kapitel 19.10, Entscheidung #39) — NACHTRÄGLICH ergänzt:
 * Offline-Konflikterkennung pro Zelle beim Veröffentlichen. Der
 * X-Expected-Last-Modified-Check bei PUT/DELETE existierte bereits als
 * generische Sync-Infrastruktur; neu hinzugekommen ist die Konflikterkennung
 * für den "Zelle war leer"-Fall (POST mit `expectEmpty`) sowie — der
 * eigentliche Kern dieser Phase — dass ein Konflikt NICHT mehr die ganze
 * Veröffentlichung abbricht: alle anderen Zellen werden weiterhin sofort
 * übernommen, nur die betroffene(n) Zelle(n) landen in einer Konflikt-Liste
 * mit Übernehmen/Verwerfen-Dialog (`ConflictDialog` unten).
 *
 * Phase T (Kapitel 19.9, Entscheidung #29) — NACHTRÄGLICH ergänzt: rotes
 * "Lücke"-Badge auf einer Zelle, wenn für diese Schicht eine Krankmeldung
 * genehmigt wurde (Person ist eingeplant, aber krankgeschrieben — reiner
 * Hinweis, kein Blocker), plus oranges "Keine Schicht"-Badge im Spalten-Kopf,
 * wenn an einem laut Location.openingHours geöffneten Tag noch niemand
 * eingeplant ist. Fehlt eine openingHours-Angabe für den Wochentag, wird
 * bewusst NICHT gewarnt (gleiche Konvention wie Kapitel 19.4/19.3).
 *
 * Phase Q (Kapitel 19.3/19.8, Entscheidung #27) — NACHTRÄGLICH ergänzt:
 * Feiertags-Erkennung im Spaltenkopf, rein clientseitig über `feiertagejs`
 * (keine Netzwerk-/API-Abhängigkeit, passt zur Offline-First-Architektur),
 * basierend auf `Location.bundesland`. Bewusst kein hartes Sperren — Zellen
 * bleiben normal antippbar, das Badge macht nur aufmerksam (Bedarf an
 * Feiertagen hängt vom Betrieb ab). Fehlt `bundesland` am Standort (z. B.
 * außerhalb Deutschlands), wird bewusst NICHT erkannt/gewarnt.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, useCanManage, useManagedLocationIds } from '../store/auth';
import {
  locationsApi,
  WEEKDAY_ORDER,
  type ApiLocation,
  type ApiShiftTemplate,
  type OpeningHoursMap,
} from '../lib/locations';
import { usersApi, type ApiUser } from '../lib/users';
import { shiftsApi, type ApiShift } from '../lib/shifts';
import { ApiError } from '../lib/api';
import { calendarEntriesApi, ENTRY_TYPE_META, type ApiCalendarEntry } from '../lib/calendarEntries';
import { shiftDurationHours, shiftHours } from '../lib/payroll';
import { formatMoney } from '../lib/format';
import { hapticError, hapticSuccess } from '../lib/haptics';
import { ChipButton } from '../components/ShiftFormModal';
import { getHolidayByDate } from 'feiertagejs';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

const hhmm = (t: string) => t.slice(0, 5);
// Gesamtprüfung 11.09.2026: lokales Datum statt UTC (sonst nach Mitternacht bis 01/02 Uhr noch „gestern“).
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtHours = (h: number) => `${h.toFixed(2).replace('.', ',')} h`;

/** Phase DD (Kapitel 19.10): "vor 12 Minuten" für den Konflikt-Dialog. */
function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  return `vor ${days} Tag${days === 1 ? '' : 'en'}`;
}

function getMondayOfWeek(year: number, isoWeek: number): Date {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = (jan4.getUTCDay() + 6) % 7;
  const week1Monday = new Date(jan4.getTime() - jan4Day * 24 * 3600 * 1000);
  return new Date(week1Monday.getTime() + (isoWeek - 1) * 7 * 24 * 3600 * 1000);
}

function currentIsoWeek(): { year: number; week: number } {
  const d = new Date();
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const firstThursdayDay = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstThursdayDay + 3);
  const week = 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
  return { year: d.getUTCFullYear(), week };
}

function formatDateISO(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
function formatDateShort(d: Date): string {
  return `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.`;
}

/** '' = kein Dienst, 'keep' = bestehende Schicht unverändert lassen,
 *  `tpl:<id>` = Vorlage, `own:<HH:MM>-<HH:MM>` = "Eigene Zeit" (Entscheidung #22c). */
type CellVal = string;

function encodeOwn(start: string, end: string): CellVal {
  return `own:${start}-${end}`;
}
function parseOwn(val: CellVal): { start: string; end: string } | null {
  if (!val.startsWith('own:')) return null;
  const [start, end] = val.slice(4).split('-');
  if (!start || !end) return null;
  return { start, end };
}

/** Kapitel 19.4 letzter Absatz: rein informativer Warnhinweis, kein Blocker.
 *  Fehlt eine Öffnungszeiten-Angabe für Standort/Wochentag, wird NICHT gewarnt. */
function isOutsideOpeningHours(
  loc: ApiLocation | undefined,
  dayIdx: number,
  start: string,
  end: string,
  isOpenEnd: boolean,
): boolean {
  const oh = (loc?.openingHours ?? null) as OpeningHoursMap | null;
  const dayKey = WEEKDAY_ORDER[dayIdx];
  const entry = oh && dayKey ? oh[dayKey] : undefined;
  if (!entry) return false;
  if (start < entry.open) return true;
  if (!isOpenEnd && end > entry.close) return true;
  return false;
}

/**
 * Phase Q (Kapitel 19.3/19.8, Entscheidung #27) — liefert den Feiertagsnamen
 * für diesen Standort/Tag, oder `null` wenn kein Bundesland gepflegt ist oder
 * der Tag kein Feiertag ist. Rein clientseitig/offline, keine Netzwerkanfrage.
 */
function holidayForDay(loc: ApiLocation | undefined, dateIso: string): string | null {
  if (!loc?.bundesland) return null;
  const holiday = getHolidayByDate(dateIso, loc.bundesland);
  if (!holiday) return null;
  return holiday.translate() ?? holiday.name;
}

/**
 * Phase T (Kapitel 19.9, Entscheidung #29) — ist der Standort an diesem
 * Wochentag laut openingHours geöffnet? Fehlt die Angabe komplett oder für
 * diesen Tag, geben wir bewusst `false` zurück (keine Aussage möglich →
 * kein "Keine Schicht"-Badge, kein falscher Alarm).
 */
function isLocationOpenOnDay(loc: ApiLocation | undefined, dayIdx: number): boolean {
  const oh = (loc?.openingHours ?? null) as OpeningHoursMap | null;
  const dayKey = WEEKDAY_ORDER[dayIdx];
  const entry = oh && dayKey ? oh[dayKey] : undefined;
  return !!(entry && entry.open && entry.close);
}

/**
 * Phase T (Kapitel 19.9, Entscheidung #29) — hat dieser User an diesem Tag
 * eine genehmigte KRANKMELDUNG (bewusst nur "sick", nicht "vacation" — Urlaub
 * ist i. d. R. lange vorher bekannt, eine Krankmeldung kommt kurzfristig und
 * ist der eigentliche Deckungslücken-Fall)?
 */
function hasApprovedSick(entries: ApiCalendarEntry[] | undefined, userId: string, dateIso: string): boolean {
  return (entries ?? []).some(
    (e) => e.userId === userId && e.date === dateIso && e.type === 'sick' && e.status === 'approved',
  );
}

type Op =
  | { kind: 'create'; userId: string; dateIso: string; tpl: ApiShiftTemplate }
  | { kind: 'createCustom'; userId: string; dateIso: string; start: string; end: string }
  | { kind: 'update'; ex: ApiShift; tpl: ApiShiftTemplate }
  | { kind: 'updateCustom'; ex: ApiShift; start: string; end: string }
  | { kind: 'delete'; ex: ApiShift };

/** Für welche Zelle (userId+dateIso) ein Op steht — für die Konflikt-Liste (Phase DD). */
function opIdentity(op: Op): { key: string; userId: string; dateIso: string } {
  if (op.kind === 'create' || op.kind === 'createCustom') {
    return { key: `${op.userId}|${op.dateIso}`, userId: op.userId, dateIso: op.dateIso };
  }
  return { key: `${op.ex.userId}|${op.ex.shiftDate}`, userId: op.ex.userId, dateIso: op.ex.shiftDate };
}

/**
 * Phase DD (Kapitel 19.10, Entscheidung #39): eine Zelle, bei der das
 * Veröffentlichen mit 409 beantwortet wurde — `currentShift` ist der
 * aktuelle Server-Stand (null bzw. `deletedAt` gesetzt = Zelle ist
 * inzwischen leer).
 */
interface Conflict {
  key: string;
  userId: string;
  dateIso: string;
  myVal: CellVal;
  currentShift: ApiShift | null;
}

type PickerTarget =
  | { mode: 'cell'; key: string; userId: string; dateIso: string; dayIdx: number }
  | { mode: 'column'; dateIso: string; dayIdx: number };

export default function WeeklyPlanPage() {
  const token = useAuth((s) => s.token)!;
  const activeCompanyId = useAuth((s) => s.activeCompanyId);
  const qc = useQueryClient();
  const isOwner = useCanManage();
  const managedLocationIds = useManagedLocationIds();
  // Nachtrag 08.09.2026 (Shadow: "Mach den Chef unsichtbar im Schichtplan,
  // der kann aber immer wieder sichtbar gemacht werden") — der Chef taucht
  // als ganz normale Zeile auf, sobald er selbst eine LocationMembership an
  // diesem Standort hat (z. B. weil er dort mitarbeitet). Standardmäßig
  // ausgeblendet, aber jederzeit per Umschalter wieder einblendbar — daher
  // bewusst NUR ein Anzeige-Filter für die Tabellenzeilen, keine dauerhafte
  // Entfernung wie bei der Chef/Admin-Ausblendung in LocationDetailPage.
  const [showChefRow, setShowChefRow] = useState(false);

  const [{ year, week }, setWeek] = useState(currentIsoWeek());
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickerTarget, setPickerTarget] = useState<PickerTarget | null>(null);

  const [choices, setChoices] = useState<Map<string, CellVal>>(new Map());
  const [baseline, setBaseline] = useState<Map<string, CellVal>>(new Map());
  // Phase DD (Kapitel 19.10, Entscheidung #39): Zellen, deren Veröffentlichen
  // mit 409 beantwortet wurde, bis zur manuellen Übernehmen/Verwerfen-Entscheidung.
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [conflictBusyKey, setConflictBusyKey] = useState<string | null>(null);
  const [conflictError, setConflictError] = useState<string | null>(null);

  const { data: locations } = useQuery({
    queryKey: ['locations', 'withTemplates'],
    queryFn: () => locationsApi.list(token, true),
  });
  const { data: users } = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list(token) });

  const locationById = useMemo(() => {
    const m = new Map<string, ApiLocation>();
    for (const l of locations ?? []) m.set(l.id, l);
    return m;
  }, [locations]);

  const templateById = useMemo(() => {
    const m = new Map<string, ApiShiftTemplate>();
    for (const l of locations ?? []) for (const t of l.templates ?? []) m.set(t.id, t);
    return m;
  }, [locations]);

  /** Phase DD (Kapitel 19.10): für die "geändert von {Name}"-Anzeige im Konflikt-Dialog. */
  const userById = useMemo(() => {
    const m = new Map<string, ApiUser>();
    for (const u of users ?? []) m.set(u.id, u);
    return m;
  }, [users]);

  /**
   * Aktive Standorte. Bis 08.09.2026 waren hier zusätzlich nur Standorte MIT
   * mindestens einer aktiven Vorlage zugelassen — Shadow wollte das nicht:
   * ein frisch angelegter Standort ohne Vorlagen soll trotzdem im Umschalter
   * auftauchen (Planung geht notfalls über den „Eigene Zeit"-Chip, siehe
   * `noTemplates`-Hinweis weiter unten statt eines Hard-Blocks).
   */
  const usableLocations = useMemo(() => (locations ?? []).filter((l) => l.isActive), [locations]);

  /** Standorte, die DIESER User hier verwalten darf (Kapitel 19.6): Chef überall,
   *  Filialleitung/Bereichsleiter nur an eigenen Standorten. */
  const myLocations = useMemo(
    () => (isOwner ? usableLocations : usableLocations.filter((l) => managedLocationIds.includes(l.id))),
    [usableLocations, isOwner, managedLocationIds],
  );

  // Nachtrag (Standort-Detail-Ansicht, 08.09.2026): Deep-Link von
  // LocationDetailPage's "Schichtplan"-Kachel — `?location=<id>` wählt den
  // Standort direkt an, statt dass man ihn nach der Navigation nochmal per
  // Umschalter suchen muss.
  const [searchParams] = useSearchParams();
  const requestedLocationId = searchParams.get('location');
  // Merkt sich, für welchen `?location=`-Wert der Deep-Link zuletzt
  // angewendet wurde (statt eines simplen Booleans) — sonst würde (a) ein
  // späterer Hintergrund-Refetch von `locations` den manuell umgeschalteten
  // Standort wieder auf den ursprünglichen Deep-Link-Wert zurückspringen
  // lassen, ABER (b) ein zweiter Deep-Link-Klick mit einem ANDEREN Standort
  // (z. B. von LocationDetailPage aus Standort B statt A geöffnet), während
  // WeeklyPlanPage schon gemountet ist — React Router remountet die
  // Komponente bei einer reinen Query-Param-Änderung nicht — wurde mit dem
  // alten Boolean-Ref stillschweigend ignoriert (Bugfix 08.09.2026,
  // Systemweiter Audit).
  const appliedDeepLinkFor = useRef<string | null>(null);

  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);
  useEffect(() => {
    setActiveLocationId((prev) => {
      if (
        requestedLocationId &&
        requestedLocationId !== appliedDeepLinkFor.current &&
        myLocations.some((l) => l.id === requestedLocationId)
      ) {
        appliedDeepLinkFor.current = requestedLocationId;
        return requestedLocationId;
      }
      if (prev && myLocations.some((l) => l.id === prev)) return prev;
      return myLocations[0]?.id ?? null;
    });
  }, [myLocations, requestedLocationId]);
  const activeLocation = activeLocationId ? locationById.get(activeLocationId) : undefined;
  const activeTemplates = useMemo(
    () => (activeLocation?.templates ?? []).filter((t) => t.isActive),
    [activeLocation],
  );

  const firstAdminId = (users ?? []).find((u) => u.role === 'admin')?.id;
  /**
   * Zeilen = aktive Mitarbeiter mit LocationMembership an DIESEM Standort
   * (Kapitel 19.6) — ersetzt `allowedLocationIds`. `u.isActive` zusätzlich
   * geprüft (Phase U+Z, Kapitel 20.3): deaktivierte Mitarbeiter verschwinden
   * aus dieser Zuordnungsliste, bleiben aber in der Mitarbeiter-Verwaltung
   * selbst sichtbar.
   */
  const locationUsers = useMemo(() => {
    if (!activeLocationId) return [];
    return (users ?? []).filter(
      (u) =>
        u.id !== firstAdminId &&
        u.isActive &&
        u.locationMemberships.some((m) => m.locationId === activeLocationId),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, firstAdminId, activeLocationId]);

  /** Chef (companyRole 'owner', bzw. Alt-'manager') DIESER aktiven Firma. */
  function isChefUser(u: ApiUser): boolean {
    return u.memberships.some((m) => m.companyId === activeCompanyId && (m.role === 'owner' || m.role === 'manager'));
  }
  const chefRows = useMemo(() => locationUsers.filter(isChefUser), [locationUsers, activeCompanyId]);
  /**
   * Zeilen, die tatsächlich GEZEICHNET werden — `locationUsers` bleibt für
   * alle Berechnungen (Tages-/Wochensummen, Lücken-Badges, Schicht-Lookup)
   * unangetastet, damit ein trotzdem geplanter Chef-Dienst weiterhin korrekt
   * mitzählt, auch während seine Zeile ausgeblendet ist.
   */
  const visibleLocationUsers = useMemo(
    () => (showChefRow ? locationUsers : locationUsers.filter((u) => !isChefUser(u))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationUsers, showChefRow, activeCompanyId],
  );

  const monday = useMemo(() => getMondayOfWeek(year, week), [year, week]);
  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday.getTime() + i * 24 * 3600 * 1000);
        const iso = formatDateISO(d);
        return { iso, short: formatDateShort(d), isToday: iso === todayIso() };
      }),
    [monday],
  );

  const { data: existingShifts } = useQuery({
    queryKey: ['shifts', 'week-all', days[0]?.iso, days[6]?.iso],
    enabled: !!days[0] && !!days[6],
    queryFn: () => shiftsApi.list(token, { from: days[0]!.iso, to: days[6]!.iso }),
  });
  // Phase T (Kapitel 19.9, Entscheidung #29) — genehmigte Krankmeldungen im
  // sichtbaren Zeitraum, für das "Lücke"-Badge. Owner/Filialleitung bekommen
  // hier ohne explizite userId automatisch alle Einträge ihres Teams
  // (siehe GET /api/calendar-entries), kein zusätzlicher Filter nötig.
  const { data: calendarEntries } = useQuery({
    queryKey: ['calendar-entries', 'week-all', days[0]?.iso, days[6]?.iso],
    enabled: !!days[0] && !!days[6],
    // Shadow 11.09.2026: „Abwesenheit soll in der Schichtplanung angezeigt
    // werden“ — deshalb genehmigte UND beantragte Einträge laden (abgelehnte
    // und Notizen werden unten ausgefiltert).
    queryFn: () => calendarEntriesApi.list(token, { from: days[0]!.iso, to: days[6]!.iso }),
  });
  /** Abwesenheiten je (Person|Tag) — genehmigt oder beantragt, ohne Notizen/Abgelehnte. */
  const absencesByKey = useMemo(() => {
    const map = new Map<string, ApiCalendarEntry[]>();
    for (const e of calendarEntries ?? []) {
      if (e.deletedAt || e.type === 'note' || e.status === 'rejected') continue;
      const key = `${e.userId}|${e.date}`;
      map.set(key, [...(map.get(key) ?? []), e]);
    }
    return map;
  }, [calendarEntries]);
  /** Ist an diesem Tag (über alle Zeilen hinweg) irgendetwas eingeplant — bestehend oder gerade gestagt? */
  function dayHasAnyPlan(dateIso: string): boolean {
    for (const u of locationUsers) {
      const key = `${u.id}|${dateIso}`;
      if ((shiftsByKey.get(key) ?? []).length > 0) return true;
      const val = choices.get(key);
      if (val) return true;
    }
    return false;
  }

  // Alle Schichten je (Mitarbeiter|Tag) — mehrere möglich, auch standortübergreifend.
  const shiftsByKey = useMemo(() => {
    const map = new Map<string, ApiShift[]>();
    for (const s of existingShifts ?? []) {
      // Gesamtprüfung 11.09.2026: abgesagte Schichten belegen keine Zelle
      // (vorher erschienen sie als normale, bearbeitbare Schicht, zählten in
      // die Stunden und blieben beim Überschreiben „abgesagt“).
      if (s.status === 'cancelled') continue;
      const key = `${s.userId}|${s.shiftDate}`;
      const arr = map.get(key) ?? [];
      arr.push(s);
      map.set(key, arr);
    }
    return map;
  }, [existingShifts]);

  function isLocked(s: ApiShift) {
    return s.status === 'done' || !!s.settledAt;
  }
  /** 'editable' | 'locked-multi' (mehrere Schichten) | 'locked-fixed' (erledigt/abgerechnet)
   *  | 'locked-foreign' (genau eine Schicht, aber an einem ANDEREN Standort — Doppelbuchungs-Schutz, s. Datei-Kommentar oben). */
  function cellStatus(
    arr: ApiShift[],
  ): 'editable' | 'locked-multi' | 'locked-fixed' | 'locked-foreign' {
    if (arr.length === 0) return 'editable';
    if (arr.length > 1) return 'locked-multi';
    const s = arr[0]!;
    if (isLocked(s)) return 'locked-fixed';
    if (activeLocationId && s.locationId !== activeLocationId) return 'locked-foreign';
    return 'editable';
  }

  // Baseline + Auswahl aufbauen, sobald Daten/Woche/Standort wechseln.
  useEffect(() => {
    if (!existingShifts || !users || !locations || !activeLocationId) {
      setBaseline(new Map());
      setChoices(new Map());
      return;
    }
    const base = new Map<string, CellVal>();
    for (const u of locationUsers) {
      for (const d of days) {
        const key = `${u.id}|${d.iso}`;
        const arr = shiftsByKey.get(key) ?? [];
        if (cellStatus(arr) !== 'editable') continue;
        const ex = arr[0];
        if (!ex) {
          base.set(key, '');
        } else if (ex.templateId && templateById.get(ex.templateId)?.isActive) {
          base.set(key, `tpl:${ex.templateId}`);
        } else {
          base.set(key, 'keep');
        }
      }
    }
    setBaseline(base);
    setChoices(new Map(base));
    setFeedback(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingShifts, users, locations, year, week, activeLocationId, locationUsers]);

  function setCell(key: string, val: CellVal) {
    setChoices((prev) => new Map(prev).set(key, val));
  }
  function fillColumn(dateIso: string, val: CellVal) {
    setChoices((prev) => {
      const n = new Map(prev);
      // Bugfix 08.09.2026 (Systemweiter Audit): `visibleLocationUsers` statt
      // `locationUsers` — sonst würde "Spalte füllen" bei ausgeblendeter
      // Chef-Zeile (Chef-Umschalter) unsichtbar auch die Chef-Zelle
      // mitbefüllen, und diese Änderung würde beim Veröffentlichen
      // klaglos mit eingeplant, ohne dass sie je sichtbar war.
      for (const u of visibleLocationUsers) {
        const key = `${u.id}|${dateIso}`;
        if (!n.has(key)) continue; // nur editierbare Zellen (s. baseline-Aufbau) werden befüllt
        n.set(key, val);
      }
      return n;
    });
  }

  async function copyPreviousWeek() {
    setBusy(true);
    setError(null);
    setFeedback(null);
    try {
      const prevMon = new Date(monday.getTime() - 7 * 24 * 3600 * 1000);
      const prevFrom = formatDateISO(prevMon);
      const prevTo = formatDateISO(new Date(prevMon.getTime() + 6 * 24 * 3600 * 1000));
      const prev = (await shiftsApi.list(token, { from: prevFrom, to: prevTo })).filter(
        (s) => !s.deletedAt && s.status !== 'cancelled' && s.locationId === activeLocationId,
      );
      // Bugfix 08.09.2026 (Systemweiter Audit): "Vorwoche kopieren" ist wie
      // "Spalte füllen" eine Massen-Schreibaktion — respektiert deshalb
      // ebenfalls den Chef-Umschalter, statt bei ausgeblendeter Chef-Zeile
      // unsichtbar dessen Schichten mit zu übernehmen.
      const visibleUserIds = new Set(visibleLocationUsers.map((u) => u.id));
      setChoices((prev0) => {
        const n = new Map(prev0);
        for (const s of prev) {
          if (!visibleUserIds.has(s.userId)) continue;
          const dObj = new Date(`${s.shiftDate}T00:00:00Z`);
          const wd = (dObj.getUTCDay() + 6) % 7;
          const targetDay = days[wd];
          if (!targetDay) continue;
          const key = `${s.userId}|${targetDay.iso}`;
          if (!n.has(key)) continue;
          if (s.templateId && templateById.get(s.templateId)?.isActive) {
            n.set(key, `tpl:${s.templateId}`);
          } else if (!s.templateId && !s.isOpenEnd) {
            n.set(key, encodeOwn(hhmm(s.startTime), hhmm(s.endTime)));
          }
        }
        return n;
      });
      setFeedback('Vorwoche übernommen — prüfen und dann veröffentlichen.');
    } catch {
      setError('Vorwoche konnte nicht geladen werden.');
    } finally {
      setBusy(false);
    }
  }

  const changedCount = useMemo(() => {
    let c = 0;
    for (const [key, val] of choices) if ((baseline.get(key) ?? '') !== val) c++;
    return c;
  }, [choices, baseline]);

  /**
   * Phase DD (Kapitel 19.10, Entscheidung #39): löst eine Zellen-Auswahl in
   * die für create/update nötigen Schicht-Felder auf. Gemeinsam genutzt vom
   * Veröffentlichen-Diff (unten) UND von `resolveConflict` ("Übernehmen").
   */
  function fieldsForVal(
    val: CellVal,
  ): { locationId: string; templateId: string | null; startTime: string; endTime: string; isOpenEnd: boolean } | null {
    if (!activeLocationId) return null;
    if (val.startsWith('tpl:')) {
      const tpl = templateById.get(val.slice(4));
      if (!tpl) return null;
      return {
        locationId: tpl.locationId,
        templateId: tpl.id,
        startTime: hhmm(tpl.startTime),
        endTime: hhmm(tpl.endTime),
        isOpenEnd: tpl.isOpenEnd,
      };
    }
    const own = parseOwn(val);
    if (own) {
      return { locationId: activeLocationId, templateId: null, startTime: own.start, endTime: own.end, isOpenEnd: false };
    }
    return null;
  }

  async function onPublish() {
    if (!activeLocationId) return;
    setBusy(true);
    setError(null);
    setFeedback(null);
    try {
      const ops: Op[] = [];
      for (const [key, val] of choices) {
        if ((baseline.get(key) ?? '') === val) continue;
        const [userId, dateIso] = key.split('|');
        if (!userId || !dateIso) continue;
        const ex = (shiftsByKey.get(key) ?? [])[0];
        const own = parseOwn(val);
        if (ex) {
          if (val === '') ops.push({ kind: 'delete', ex });
          else if (val.startsWith('tpl:')) {
            const tpl = templateById.get(val.slice(4));
            if (tpl) ops.push({ kind: 'update', ex, tpl });
          } else if (own) {
            ops.push({ kind: 'updateCustom', ex, start: own.start, end: own.end });
          }
        } else if (val.startsWith('tpl:')) {
          const tpl = templateById.get(val.slice(4));
          if (tpl) ops.push({ kind: 'create', userId, dateIso, tpl });
        } else if (own) {
          ops.push({ kind: 'createCustom', userId, dateIso, start: own.start, end: own.end });
        }
      }
      if (ops.length === 0) {
        setFeedback('Keine Änderungen.');
        return;
      }
      let created = 0;
      let updated = 0;
      let deleted = 0;
      let failed = 0;
      // Phase DD (Kapitel 19.10, Entscheidung #39): ein Konflikt bei EINER
      // Zelle darf die Veröffentlichung der anderen nicht blockieren — jedes
      // Op läuft daher mit eigenem try/catch; ein 409 landet in einer
      // separaten Konflikt-Liste statt die Schleife abzubrechen.
      const newConflicts: Conflict[] = [];
      // Gesamtprüfung 11.09.2026: Einplanen trotz genehmigtem Urlaub/Krank
      // (Kapitel 5.2) — der Server antwortet mit 409 + Code; vorher landete das
      // als „Offline-Konflikt“ ohne Lösung. Jetzt: gesammelt nachfragen und
      // bei Bestätigung mit vacationOverride erneut anlegen.
      const absenceOps: { op: Op; message: string }[] = [];
      const failureMessages: string[] = [];
      const createOp = async (op: Op, vacationOverride: boolean) => {
        if (op.kind === 'create') {
          await shiftsApi.create(token, {
            userId: op.userId,
            locationId: op.tpl.locationId,
            templateId: op.tpl.id,
            shiftDate: op.dateIso,
            startTime: hhmm(op.tpl.startTime),
            endTime: hhmm(op.tpl.endTime),
            status: 'planned',
            isOpenEnd: op.tpl.isOpenEnd,
            expectEmpty: true,
            ...(vacationOverride ? { vacationOverride: true } : {}),
          });
        } else if (op.kind === 'createCustom') {
          await shiftsApi.create(token, {
            userId: op.userId,
            locationId: activeLocationId,
            templateId: null,
            shiftDate: op.dateIso,
            startTime: op.start,
            endTime: op.end,
            status: 'planned',
            expectEmpty: true,
            ...(vacationOverride ? { vacationOverride: true } : {}),
          });
        }
      };
      const isAbsenceConflict = (err: unknown) =>
        err instanceof ApiError &&
        err.status === 409 &&
        (err.body as { details?: { code?: string } } | null)?.details?.code === 'APPROVED_ABSENCE_CONFLICT';
      for (const op of ops) {
        try {
          if (op.kind === 'create' || op.kind === 'createCustom') {
            try {
              await createOp(op, false);
            } catch (err) {
              if (isAbsenceConflict(err)) {
                absenceOps.push({ op, message: (err as ApiError).message });
                continue;
              }
              throw err;
            }
            created++;
          } else if (op.kind === 'update') {
            await shiftsApi.update(
              token,
              op.ex.id,
              {
                locationId: op.tpl.locationId,
                templateId: op.tpl.id,
                startTime: hhmm(op.tpl.startTime),
                endTime: hhmm(op.tpl.endTime),
                isOpenEnd: op.tpl.isOpenEnd,
              },
              op.ex.lastModified,
            );
            updated++;
          } else if (op.kind === 'updateCustom') {
            await shiftsApi.update(
              token,
              op.ex.id,
              {
                locationId: activeLocationId,
                templateId: null,
                startTime: op.start,
                endTime: op.end,
                isOpenEnd: false,
              },
              op.ex.lastModified,
            );
            updated++;
          } else {
            await shiftsApi.remove(token, op.ex.id, op.ex.lastModified);
            deleted++;
          }
        } catch (err) {
          if (err instanceof ApiError && err.status === 409) {
            const { key, userId, dateIso } = opIdentity(op);
            const body = err.body as { shift?: ApiShift | null } | null;
            newConflicts.push({
              key,
              userId,
              dateIso,
              myVal: choices.get(key) ?? '',
              currentShift: body?.shift ?? null,
            });
          } else {
            failed++;
            // Gesamtprüfung 11.09.2026: Grund anzeigen (z. B. Doppelschicht).
            if (err instanceof Error && failureMessages.length < 3) failureMessages.push(err.message);
          }
        }
      }
      if (absenceOps.length > 0) {
        const list = absenceOps.map((a) => `• ${a.message}`).join('\n');
        if (confirm(`Genehmigte Abwesenheit:\n\n${list}\n\nTrotzdem einplanen?`)) {
          for (const { op } of absenceOps) {
            try {
              await createOp(op, true);
              created++;
            } catch (err) {
              failed++;
              if (err instanceof Error && failureMessages.length < 3) failureMessages.push(err.message);
            }
          }
        }
      }
      const parts: string[] = [];
      if (created) parts.push(`${created} neu`);
      if (updated) parts.push(`${updated} geändert`);
      if (deleted) parts.push(`${deleted} entfernt`);
      let msg = parts.length > 0 ? `Veröffentlicht: ${parts.join(', ')}.` : 'Keine Änderungen übernommen.';
      if (newConflicts.length > 0) {
        msg += ` ${newConflicts.length} Konflikt${newConflicts.length === 1 ? '' : 'e'} — bitte unten prüfen.`;
      }
      if (failed > 0) {
        msg += ` ${failed} Fehler${failureMessages.length > 0 ? `: ${failureMessages.join(' · ')}` : '.'}`;
      }
      if (newConflicts.length > 0 || failed > 0) {
        hapticError();
      } else {
        hapticSuccess();
      }
      if (newConflicts.length > 0) setConflicts((prev) => [...prev, ...newConflicts]);
      setFeedback(msg);
      await qc.invalidateQueries({ queryKey: ['shifts', 'week-all', days[0]?.iso, days[6]?.iso] });
      // Gruppe F (Kapitel 19.9b, Entscheidung #42): einmal nach dem Durchlauf
      // serverseitig prüfen lassen, ob der veröffentlichte Plan noch offene
      // Tage ohne Schicht hat — rein informativ, blockiert die UI nicht.
      if (created + updated + deleted > 0 && days[0] && days[6]) {
        shiftsApi.notifyPublishGaps(token, activeLocationId, days[0].iso, days[6].iso).catch(() => {});
      }
    } catch (err) {
      hapticError();
      setError(err instanceof Error ? err.message : 'Veröffentlichen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Phase DD (Kapitel 19.10, Entscheidung #39): "Übernehmen" überschreibt den
   * aktuellen Server-Stand mit der eigenen Änderung, "Verwerfen" lässt ihn
   * stehen und wirft die eigene Änderung für diese Zelle weg. Hat sich der
   * Stand zwischen dem ursprünglichen Konflikt und dem Klick auf
   * "Übernehmen" SCHON WIEDER geändert, löst das erneut in einen (dann
   * aktualisierten) Konflikt auf, statt die Zeile stumm zu überschreiben.
   */
  async function resolveConflict(c: Conflict, decision: 'apply' | 'discard') {
    if (decision === 'discard') {
      setConflicts((prev) => prev.filter((x) => x.key !== c.key));
      return;
    }
    setConflictBusyKey(c.key);
    setConflictError(null);
    try {
      const stillOccupied = !!c.currentShift && !c.currentShift.deletedAt;
      if (stillOccupied) {
        if (c.myVal === '') {
          await shiftsApi.remove(token, c.currentShift!.id, c.currentShift!.lastModified);
        } else {
          const fields = fieldsForVal(c.myVal);
          // Gruppe F (Kapitel 19.10, Entscheidung #39/#42): "Übernehmen"
          // überschreibt bewusst den Server-Stand — löst serverseitig die
          // "Änderung überschrieben"-Push an die ursprüngliche Person aus.
          if (fields) {
            await shiftsApi.update(
              token,
              c.currentShift!.id,
              { ...fields, conflictOverride: true },
              c.currentShift!.lastModified,
            );
          }
        }
      } else if (c.myVal !== '') {
        const fields = fieldsForVal(c.myVal);
        if (fields) {
          await shiftsApi.create(token, {
            userId: c.userId,
            shiftDate: c.dateIso,
            status: 'planned',
            expectEmpty: true,
            ...fields,
          });
        }
      }
      hapticSuccess();
      setConflicts((prev) => prev.filter((x) => x.key !== c.key));
      await qc.invalidateQueries({ queryKey: ['shifts', 'week-all', days[0]?.iso, days[6]?.iso] });
    } catch (err) {
      hapticError();
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { shift?: ApiShift | null } | null;
        setConflicts((prev) => prev.map((x) => (x.key === c.key ? { ...x, currentShift: body?.shift ?? null } : x)));
        setConflictError('Der Stand hat sich inzwischen erneut geändert — bitte den neuen Vergleich prüfen.');
      } else {
        setConflictError(err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen — bitte erneut versuchen.');
      }
    } finally {
      setConflictBusyKey(null);
    }
  }

  // --- Live-Stunden ---
  function cellHours(userId: string, dateIso: string): number {
    const key = `${userId}|${dateIso}`;
    const arr = shiftsByKey.get(key) ?? [];
    const status = cellStatus(arr);
    if (status === 'locked-foreign') return 0; // gehört nicht zum Plan DIESES Standorts
    if (status !== 'editable') return arr.reduce((a, s) => a + shiftHours(s), 0);
    const ex = arr[0];
    const val = choices.get(key) ?? '';
    if (val === '') return 0;
    if (val === 'keep') return ex ? shiftHours(ex) : 0;
    if (val.startsWith('tpl:')) {
      const t = templateById.get(val.slice(4));
      if (t) return shiftDurationHours({ startTime: hhmm(t.startTime), endTime: hhmm(t.endTime), isOpenEnd: t.isOpenEnd });
    }
    const own = parseOwn(val);
    if (own) return shiftDurationHours({ startTime: own.start, endTime: own.end, isOpenEnd: false });
    return 0;
  }
  const rowHours = (userId: string) => days.reduce((a, d) => a + cellHours(userId, d.iso), 0);

  const totals = useMemo(() => {
    let hours = 0;
    let wage = 0;
    // Phase G (Entscheidung #6): hourlyWage ist `null`, wenn diese Filialleitung
    // für den/die Mitarbeiter:in keine Lohn-Sichtbarkeit hat — solche Zeilen
    // fließen NICHT in die Summe ein; `wageIncomplete` markiert das.
    let wageIncomplete = false;
    // Bewusst `visibleLocationUsers` statt `locationUsers` (Nachtrag
    // 08.09.2026, Chef-Umschalter): die "Gesamt"-Zeile soll zu den
    // tatsächlich sichtbaren Zeilen passen, sonst wirkt eine Summe, die
    // größer ist als alles Sichtbare zusammen, wie ein Rechenfehler.
    for (const u of visibleLocationUsers) {
      const h = rowHours(u.id);
      hours += h;
      if (u.hourlyWage == null) {
        if (h > 0) wageIncomplete = true;
      } else {
        wage += h * u.hourlyWage;
      }
    }
    return { hours, wage, wageIncomplete };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleLocationUsers, choices, shiftsByKey, days]);

  function shiftWeek(delta: number) {
    const newMonday = new Date(monday.getTime() + delta * 7 * 24 * 3600 * 1000);
    const d = new Date(newMonday);
    d.setUTCDate(d.getUTCDate() + 3);
    const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
    const firstThuDay = (firstThu.getUTCDay() + 6) % 7;
    firstThu.setUTCDate(firstThu.getUTCDate() - firstThuDay + 3);
    const newWeek = 1 + Math.round((d.getTime() - firstThu.getTime()) / (7 * 24 * 3600 * 1000));
    setWeek({ year: d.getUTCFullYear(), week: newWeek });
  }

  function cellLabel(val: CellVal, baselineVal: CellVal | undefined, ex: ApiShift | undefined): string {
    if (val === '') return '—';
    if (val === 'keep' && ex) return `${hhmm(ex.startTime)}${ex.isOpenEnd ? '+' : `–${hhmm(ex.endTime)}`}`;
    if (val.startsWith('tpl:')) return templateById.get(val.slice(4))?.name ?? '?';
    const own = parseOwn(val);
    if (own) return `${own.start}–${own.end}`;
    return baselineVal ?? '—';
  }

  const noLocations = myLocations.length === 0;
  const noTemplates = !!activeLocation && activeTemplates.length === 0;

  return (
    <div className="max-w-5xl mx-auto space-y-4 pb-8">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h2 className="text-xl font-bold">Wochenplan</h2>
        {activeLocation && <span className="text-xs text-slate-400">{activeLocation.name}</span>}
      </div>

      {/* Kopf: Woche + Aktionen + (bei mehreren Standorten) Umschalter — Kapitel 19.3 */}
      <div className="card p-3 flex flex-wrap items-center gap-3">
        <button
          onClick={() => void copyPreviousWeek()}
          disabled={busy || !activeLocationId}
          className="btn-ghost text-xs px-3 py-1.5"
        >
          ⧉ Vorwoche kopieren
        </button>

        {/* Nachtrag 08.09.2026 (Shadow): Chef ist standardmäßig ausgeblendet,
            falls er selbst diesem Standort zugeordnet ist — jederzeit
            umschaltbar, keine dauerhafte Entfernung. */}
        {chefRows.length > 0 && (
          <button
            onClick={() => setShowChefRow((v) => !v)}
            className="btn-ghost text-xs px-3 py-1.5"
          >
            {showChefRow ? 'Chef ausblenden' : 'Chef anzeigen'}
          </button>
        )}

        <div className="flex items-center gap-1 ml-auto">
          <button onClick={() => shiftWeek(-1)} className="btn-ghost px-3 py-1" aria-label="Vorherige Woche">
            ‹
          </button>
          <span className="text-sm px-2 whitespace-nowrap">
            KW {week} · {year}
          </span>
          <button onClick={() => shiftWeek(+1)} className="btn-ghost px-3 py-1" aria-label="Nächste Woche">
            ›
          </button>
        </div>
      </div>

      {myLocations.length > 1 && (
        <div className="card p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-400 mr-1">Standort:</span>
            {myLocations.map((l) => {
              const on = l.id === activeLocationId;
              return (
                <button
                  key={l.id}
                  onClick={() => setActiveLocationId(l.id)}
                  className={`text-xs rounded-full px-3 py-1 !min-h-0 border transition ${
                    on ? 'text-white border-transparent' : 'text-slate-300 border-border bg-surface-3'
                  }`}
                  style={on ? { backgroundColor: l.color } : undefined}
                  aria-pressed={on}
                >
                  {l.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {noLocations && (
        <p className="p-4 card text-sm text-amber-300">Keine Standorte zugeordnet.</p>
      )}

      {/* Nachtrag 08.09.2026: fehlende Vorlagen blockieren den Plan nicht
          mehr komplett (Shadow: Standorte ohne Vorlagen müssen trotzdem
          planbar sein) — nur noch ein Hinweis, „Eigene Zeit" im Picker geht
          immer, unabhängig von Vorlagen. */}
      {!noLocations && noTemplates && (
        <p className="p-3 card text-xs text-amber-300">
          Für {activeLocation?.name} sind noch keine aktiven Schicht-Vorlagen vorhanden —
          Schichten lassen sich trotzdem über „Eigene Zeit" im Picker eintragen. Vorlagen legst
          du unter „Standorte &amp; Schicht-Vorlagen" an.
        </p>
      )}

      {!noLocations && locationUsers.length === 0 && (
        <p className="card p-4 text-sm text-slate-400">
          Keine Mitarbeiter mit Zuordnung zu {activeLocation?.name}.
        </p>
      )}

      {/* Nur der Chef ist hier zugeordnet, seine Zeile aber ausgeblendet — ohne
          diesen Hinweis stünde sonst fälschlich "keine Mitarbeiter" da. */}
      {!noLocations && locationUsers.length > 0 && visibleLocationUsers.length === 0 && (
        <p className="card p-4 text-sm text-slate-400">
          Nur der Chef ist {activeLocation?.name} zugeordnet, seine Zeile ist ausgeblendet.{' '}
          <button onClick={() => setShowChefRow(true)} className="text-brand hover:underline !min-h-0">
            Chef anzeigen
          </button>
        </p>
      )}

      {/* Tabelle */}
      {!noLocations && visibleLocationUsers.length > 0 && (
        <div className="card overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-3 py-2 sticky left-0 bg-surface z-10 min-w-[9rem]">
                  Mitarbeiter
                </th>
                {days.map((d, i) => {
                  const holidayName = holidayForDay(activeLocation, d.iso);
                  const showNoShiftBadge =
                    !holidayName && isLocationOpenOnDay(activeLocation, i) && !dayHasAnyPlan(d.iso);
                  return (
                    <th
                      key={d.iso}
                      className={`px-2 py-2 text-center whitespace-nowrap ${d.isToday ? 'bg-brand/10' : ''}`}
                    >
                      <div className={`text-xs ${d.isToday ? 'text-brand-light font-semibold' : 'text-slate-400'}`}>
                        {WEEKDAYS[i]}
                      </div>
                      <div className="text-xs font-medium">{d.short}</div>
                      {holidayName && (
                        <div
                          className="mt-0.5 text-[9px] leading-tight text-amber-400 truncate max-w-[4.5rem] mx-auto"
                          title={`Feiertag: ${holidayName}`}
                        >
                          ★ Feiertag
                        </div>
                      )}
                      {showNoShiftBadge && (
                        <div
                          className="mt-0.5 text-[9px] leading-tight text-orange-400 truncate max-w-[4.5rem] mx-auto"
                          title="Standort ist an diesem Tag geöffnet, aber noch niemand eingeplant."
                        >
                          ⚠ Keine Schicht
                        </div>
                      )}
                      <button
                        onClick={() => setPickerTarget({ mode: 'column', dateIso: d.iso, dayIdx: i })}
                        className="mt-1 text-[10px] bg-slate-900 border border-border rounded px-1 py-0.5 !min-h-0 w-full text-slate-300 hover:bg-slate-800"
                        title="Ganze Spalte füllen"
                      >
                        Spalte ▾
                      </button>
                    </th>
                  );
                })}
                <th className="px-3 py-2 text-right whitespace-nowrap min-w-[7rem]">Geplant</th>
              </tr>
            </thead>
            <tbody>
              {visibleLocationUsers.map((u) => {
                const h = rowHours(u.id);
                return (
                  <tr key={u.id} className="border-b border-border/50">
                    <td className="px-3 py-2 sticky left-0 bg-surface z-10 whitespace-nowrap font-medium">
                      {u.name}
                    </td>
                    {days.map((d, i) => {
                      const key = `${u.id}|${d.iso}`;
                      const arr = shiftsByKey.get(key) ?? [];
                      const status = cellStatus(arr);
                      const val = choices.get(key) ?? '';
                      const changed = baseline.has(key) && (baseline.get(key) ?? '') !== val;
                      // Phase T (Kapitel 19.9, Entscheidung #29): eine Schicht steht,
                      // aber die Person ist an diesem Tag krankgeschrieben — reiner
                      // Hinweis, blockiert nichts.
                      const sickGap = arr.length > 0 && hasApprovedSick(calendarEntries, u.id, d.iso);
                      return (
                        <td
                          key={d.iso}
                          className={`relative px-1 py-1 text-center ${d.isToday ? 'bg-brand/5' : ''}`}
                        >
                          {sickGap && (
                            <span
                              className="absolute top-0 right-0.5 text-[10px] leading-none text-red-400"
                              title="Lücke: für diese Schicht ist eine Krankmeldung genehmigt."
                            >
                              ⚠
                            </span>
                          )}
                          {(absencesByKey.get(key) ?? []).map((a) => {
                            const meta = ENTRY_TYPE_META[a.type];
                            const pending = a.status === 'pending';
                            return (
                              <span
                                key={a.id}
                                className={`block text-[10px] leading-tight font-medium mb-0.5 truncate ${pending ? 'opacity-70 italic' : ''}`}
                                style={{ color: meta.color }}
                                title={`${meta.label}${a.halfDay ? ' (halber Tag)' : ''}${pending ? ' — beantragt, noch nicht genehmigt' : ' — genehmigt'}${a.title ? `: ${a.title}` : ''}`}
                              >
                                {meta.short}
                                {a.halfDay ? ' ½' : ''}
                                {pending ? ' ?' : ''}
                              </span>
                            );
                          })}
                          {status !== 'editable' ? (
                            <LockedCell
                              arr={arr}
                              locationById={locationById}
                              reason={
                                status === 'locked-multi'
                                  ? 'multi'
                                  : status === 'locked-foreign'
                                    ? 'foreign'
                                    : 'fixed'
                              }
                            />
                          ) : (
                            <button
                              onClick={() =>
                                setPickerTarget({ mode: 'cell', key, userId: u.id, dateIso: d.iso, dayIdx: i })
                              }
                              className={`text-xs rounded px-2 py-1.5 !min-h-0 w-full border transition ${
                                changed ? 'ring-1 ring-amber-400/70' : ''
                              } ${
                                val
                                  ? 'bg-slate-900 text-slate-100 border-brand/50'
                                  : 'bg-surface-3 text-slate-400 border-border hover:bg-slate-700'
                              }`}
                            >
                              {cellLabel(val, baseline.get(key), arr[0])}
                            </button>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <span className="text-slate-200">{fmtHours(h)}</span>
                      <span className="block text-[11px] text-emerald-400">
                        {u.hourlyWage == null ? '–' : formatMoney(h * u.hourlyWage)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-border bg-surface-2/40">
                <td className="px-3 py-2 sticky left-0 bg-surface-2 z-10 font-medium">Gesamt</td>
                {days.map((d) => {
                  const dayH = visibleLocationUsers.reduce((a, u) => a + cellHours(u.id, d.iso), 0);
                  return (
                    <td key={d.iso} className="px-1 py-2 text-center text-[11px] text-slate-400">
                      {dayH > 0 ? fmtHours(dayH) : '—'}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <span className="font-semibold text-slate-100">{fmtHours(totals.hours)}</span>
                  <span className="block text-[11px] text-emerald-400">
                    {formatMoney(totals.wage)}
                    {totals.wageIncomplete && (
                      <span className="text-amber-300" title="Lohn für mindestens eine Person nicht sichtbar (Standort-Einstellung)">
                        {' '}*
                      </span>
                    )}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Aktionen */}
      <div className="card p-3 flex flex-wrap items-center gap-3">
        <div className="text-sm text-slate-400">
          {changedCount === 0
            ? 'Keine offenen Änderungen.'
            : `${changedCount} Änderung(en) bereit zum Veröffentlichen`}
        </div>
        <button onClick={() => void onPublish()} disabled={busy || changedCount === 0} className="btn ml-auto">
          {busy ? 'Veröffentliche…' : 'Wochenplan veröffentlichen'}
        </button>
      </div>

      {feedback && <p className="text-sm text-emerald-400">{feedback}</p>}
      {error && <p className="text-sm text-red-400">{error}</p>}

      <p className="text-xs text-slate-500">
        Tippe eine Zelle an, um Vorlage, „Eigene Zeit" oder „kein Dienst" zu wählen. Bearbeiten
        geht auch nach dem Veröffentlichen jederzeit weiter (Kapitel 19.7) — erledigte,
        abgerechnete, mehrfach belegte oder an einem anderen Standort eingeplante Tage sind
        gesperrt.
      </p>

      {conflicts.length > 0 && (
        <ConflictDialog
          conflicts={conflicts}
          userById={userById}
          templateById={templateById}
          busyKey={conflictBusyKey}
          error={conflictError}
          onResolve={resolveConflict}
          onClose={() => setConflicts([])}
        />
      )}

      {pickerTarget && activeLocationId && (
        <CellPicker
          target={pickerTarget}
          templates={activeTemplates}
          location={activeLocation}
          currentVal={pickerTarget.mode === 'cell' ? choices.get(pickerTarget.key) ?? '' : ''}
          hasKeep={pickerTarget.mode === 'cell' && baseline.get(pickerTarget.key) === 'keep'}
          keepLabel={
            pickerTarget.mode === 'cell'
              ? (() => {
                  const ex = (shiftsByKey.get(pickerTarget.key) ?? [])[0];
                  return ex ? `aktuell ${hhmm(ex.startTime)}${ex.isOpenEnd ? '+' : `–${hhmm(ex.endTime)}`}` : undefined;
                })()
              : undefined
          }
          onSelect={(val) => {
            if (pickerTarget.mode === 'cell') setCell(pickerTarget.key, val);
            else fillColumn(pickerTarget.dateIso, val);
          }}
          onClose={() => setPickerTarget(null)}
        />
      )}
    </div>
  );
}

function LockedCell({
  arr,
  locationById,
  reason,
}: {
  arr: ApiShift[];
  locationById: Map<string, ApiLocation>;
  reason: 'multi' | 'fixed' | 'foreign';
}) {
  if (reason === 'multi') {
    return (
      <div className="text-[11px] px-2 py-1 rounded bg-slate-900/60 text-slate-300" title="Mehrere Schichten an diesem Tag">
        {arr.length}× Dienste
        <span className="block text-[9px] text-slate-500">gesperrt</span>
      </div>
    );
  }
  const ex = arr[0]!;
  const color = locationById.get(ex.locationId)?.color ?? '#64748b';
  if (reason === 'foreign') {
    const locName = locationById.get(ex.locationId)?.name ?? '?';
    return (
      <div
        className="text-[11px] px-2 py-1 rounded bg-slate-900/60 border-l-2 text-slate-400"
        style={{ borderColor: color }}
        title={`Bereits an ${locName} eingeplant`}
      >
        {hhmm(ex.startTime)}
        {ex.isOpenEnd ? '+' : `–${hhmm(ex.endTime)}`}
        <span className="block text-[9px] text-slate-500 truncate">{locName}</span>
      </div>
    );
  }
  return (
    <div
      className="text-[11px] px-2 py-1 rounded bg-slate-900/60 border-l-2 text-slate-300"
      style={{ borderColor: color }}
      title={ex.settledAt ? 'Abgerechnet — fix' : 'Erledigt — fix'}
    >
      {hhmm(ex.startTime)}
      {ex.isOpenEnd ? '+' : `–${hhmm(ex.endTime)}`}
      <span className="block text-[9px] text-slate-500">fix</span>
    </div>
  );
}

/**
 * Tap-Chip-Picker (Kapitel 19.4) — ersetzt das native `<select>` sowohl für
 * einzelne Zellen als auch für „Spalte füllen" (per `target.mode`). Auf
 * schmalen Bildschirmen als Bottom-Sheet, auf breiten zentriert — dieselbe
 * Modal-Darstellung wie `ShiftFormModal`/`LocationsAdminPage`.
 */
/**
 * Phase DD (Kapitel 19.10, Entscheidung #39): Konflikt-Liste mit
 * Übernehmen/Verwerfen pro Zelle — "einfacher Vergleich" laut Konzept,
 * keine automatische Zusammenführung.
 */
function ConflictDialog({
  conflicts,
  userById,
  templateById,
  busyKey,
  error,
  onResolve,
  onClose,
}: {
  conflicts: Conflict[];
  userById: Map<string, ApiUser>;
  templateById: Map<string, ApiShiftTemplate>;
  busyKey: string | null;
  error: string | null;
  onResolve: (c: Conflict, decision: 'apply' | 'discard') => void;
  onClose: () => void;
}) {
  function myLabel(val: CellVal): string {
    if (val === '') return '— kein Dienst';
    if (val.startsWith('tpl:')) return templateById.get(val.slice(4))?.name ?? '?';
    const own = parseOwn(val);
    if (own) return `${own.start}–${own.end}`;
    return '?';
  }
  function currentLabel(s: ApiShift | null): string {
    if (!s || s.deletedAt) return 'leer';
    const time = `${hhmm(s.startTime)}${s.isOpenEnd ? '+' : `–${hhmm(s.endTime)}`}`;
    const who = s.lastModifiedBy ? userById.get(s.lastModifiedBy)?.name : undefined;
    const when = relativeTime(s.lastModified);
    return who ? `${time}, geändert von ${who} ${when}` : `${time}, geändert ${when}`;
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-lg max-h-[85vh] overflow-y-auto space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold">
            {conflicts.length} Konflikt{conflicts.length === 1 ? '' : 'e'} beim Veröffentlichen
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200 text-sm px-1">
            ✕
          </button>
        </div>
        <p className="text-xs text-slate-400">
          Diese Zellen wurden zwischenzeitlich von jemand anderem geändert. Alle anderen Zellen
          wurden bereits veröffentlicht — wähle pro Zelle, ob deine Änderung übernommen oder
          verworfen werden soll.
        </p>
        <ul className="space-y-2">
          {conflicts.map((c) => {
            const user = userById.get(c.userId);
            const busy = busyKey === c.key;
            return (
              <li key={c.key} className="rounded border border-slate-700 p-3 space-y-2">
                <p className="text-sm font-medium">
                  {user?.name ?? 'Unbekannt'} · {c.dateIso.slice(8, 10)}.{c.dateIso.slice(5, 7)}.
                </p>
                <p className="text-xs text-slate-400">Dein Stand: {myLabel(c.myVal)}</p>
                <p className="text-xs text-slate-400">Aktueller Stand: {currentLabel(c.currentShift)}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => onResolve(c, 'apply')}
                    disabled={busy}
                    className="btn-ghost text-xs px-3 py-1.5 disabled:opacity-50"
                  >
                    {busy ? '…' : 'Übernehmen'}
                  </button>
                  <button
                    onClick={() => onResolve(c, 'discard')}
                    disabled={busy}
                    className="btn-ghost text-xs px-3 py-1.5 disabled:opacity-50"
                  >
                    Verwerfen
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        {error && <p className="text-xs text-red-400">{error}</p>}
      </div>
    </div>
  );
}

function CellPicker({
  target,
  templates,
  location,
  currentVal,
  hasKeep,
  keepLabel,
  onSelect,
  onClose,
}: {
  target: PickerTarget;
  templates: ApiShiftTemplate[];
  location: ApiLocation | undefined;
  currentVal: CellVal;
  hasKeep: boolean;
  keepLabel: string | undefined;
  onSelect: (val: CellVal) => void;
  onClose: () => void;
}) {
  const [customMode, setCustomMode] = useState(false);
  const [customStart, setCustomStart] = useState('09:00');
  const [customEnd, setCustomEnd] = useState('17:00');

  function choose(val: CellVal) {
    onSelect(val);
    onClose();
  }

  const title =
    target.mode === 'column'
      ? `Ganze Spalte: ${target.dateIso.slice(8, 10)}.${target.dateIso.slice(5, 7)}.`
      : `${target.dateIso.slice(8, 10)}.${target.dateIso.slice(5, 7)}. — Schicht wählen`;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-sm max-h-[85vh] overflow-y-auto">
        <h3 className="text-base font-semibold mb-3">{title}</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          <ChipButton active={currentVal === ''} onClick={() => choose('')}>
            — kein Dienst
          </ChipButton>
          {hasKeep && (
            <ChipButton active={currentVal === 'keep'} onClick={() => choose('keep')}>
              {keepLabel ?? 'aktuell behalten'}
            </ChipButton>
          )}
          {templates.map((t) => {
            const warn = isOutsideOpeningHours(
              location,
              target.dayIdx,
              hhmm(t.startTime),
              hhmm(t.endTime),
              t.isOpenEnd,
            );
            return (
              <ChipButton
                key={t.id}
                active={currentVal === `tpl:${t.id}`}
                onClick={() => choose(`tpl:${t.id}`)}
                title={warn ? 'Außerhalb der gepflegten Öffnungszeiten' : undefined}
              >
                {t.name} · {hhmm(t.startTime)}
                {t.isOpenEnd ? '+' : `–${hhmm(t.endTime)}`}
                {warn ? ' ⚠' : ''}
              </ChipButton>
            );
          })}
          <ChipButton active={customMode || currentVal.startsWith('own:')} onClick={() => setCustomMode(true)}>
            Eigene Zeit
          </ChipButton>
        </div>

        {customMode && (
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <input
              type="time"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="input !py-1 !px-2 w-28"
            />
            <span className="text-slate-500">–</span>
            <input
              type="time"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="input !py-1 !px-2 w-28"
            />
            <button
              onClick={() => choose(encodeOwn(customStart, customEnd))}
              className="btn text-xs px-3 py-1.5 !min-h-0"
            >
              Übernehmen
            </button>
          </div>
        )}

        <button onClick={onClose} className="btn-ghost text-xs w-full mt-1">
          Abbrechen
        </button>
      </div>
    </div>
  );
}
